export const STATUS_STYLES = {
  "New Lead": "bg-navy-900 text-white",
  Contacted: "bg-sky-100 text-sky-900",
  "Home Visit Booked": "bg-emerald-100 text-emerald-900",
  "Home Visit Completed": "bg-teal-100 text-teal-900",
  "Quote Created": "bg-amber-100 text-amber-900",
  "Quote Sent": "bg-amber-200 text-amber-900",
  "Follow-Up": "bg-orange-100 text-orange-900",
  "Quote Accepted": "bg-emerald-600 text-white",
  "Deposit Paid": "bg-emerald-100 text-emerald-900",
  "Order Confirmed": "bg-navy-900 text-white",
  Manufacturing: "bg-indigo-100 text-indigo-900",
  "Installation Booked": "bg-sky-100 text-sky-900",
  Installed: "bg-emerald-100 text-emerald-900",
  "Balance Paid": "bg-emerald-100 text-emerald-900",
  Completed: "bg-emerald-700 text-white",
  "Review Requested": "bg-slate-100 text-slate-600",
  Cancelled: "bg-slate-200 text-slate-600",
  Lost: "bg-slate-200 text-slate-600",
  "On Hold": "bg-slate-200 text-slate-600",
};
export const STATUSES = Object.keys(STATUS_STYLES);
export const statusStyle = (s) => STATUS_STYLES[s] || "bg-slate-100 text-slate-600";
export const slug = (s) => (s || "").toLowerCase().replace(/[^a-z]+/g, "-");

// Pipeline stages group the 19 statuses for the board view
export const STAGES = [
  ["New", ["New Lead", "Contacted"]],
  ["Visits", ["Home Visit Booked", "Home Visit Completed"]],
  ["Quotes", ["Quote Created", "Quote Sent", "Follow-Up", "Quote Accepted"]],
  ["Production", ["Deposit Paid", "Order Confirmed", "Manufacturing"]],
  ["Completion", ["Installation Booked", "Installed", "Balance Paid", "Completed", "Review Requested"]],
  ["Closed", ["Cancelled", "Lost", "On Hold"]],
];

// --- Simple statuses for non-technical staff (full pipeline + HubSpot keep running underneath) ---
export const SIMPLE_STATUSES = [
  "New Enquiry", "Visit Booked", "Visit Done", "Quoted",
  "Order Placed", "Install Booked", "Done", "Not Going Ahead",
];
const LEAD_TO_SIMPLE = {
  "New Lead": "New Enquiry", Contacted: "New Enquiry",
  "Home Visit Booked": "Visit Booked", "Home Visit Completed": "Visit Done",
  "Quote Created": "Quoted", "Quote Sent": "Quoted", "Follow-Up": "Quoted",
  "Quote Accepted": "Order Placed", "Deposit Paid": "Order Placed",
  "Order Confirmed": "Order Placed", Manufacturing: "Order Placed",
  "Installation Booked": "Install Booked",
  Installed: "Done", "Balance Paid": "Done", Completed: "Done", "Review Requested": "Done",
  Cancelled: "Not Going Ahead", Lost: "Not Going Ahead", "On Hold": "Not Going Ahead",
};
const SIMPLE_TO_LEAD = {
  "New Enquiry": "New Lead", "Visit Booked": "Home Visit Booked",
  "Visit Done": "Home Visit Completed", Quoted: "Quote Sent",
  "Order Placed": "Order Confirmed", "Install Booked": "Installation Booked",
  Done: "Completed", "Not Going Ahead": "Lost",
};
export const simpleStatus = (s) => LEAD_TO_SIMPLE[s] || s || "New Enquiry";
export const leadStatusFor = (simple) => SIMPLE_TO_LEAD[simple] || simple;

export const SIMPLE_ORDER_STATUSES = ["Order Placed", "Making", "Install Booked", "Installed", "Done"];
const ORDER_TO_SIMPLE = {
  "Awaiting Deposit": "Order Placed", "Deposit Paid": "Order Placed", "Order Confirmed": "Order Placed",
  Manufacturing: "Making", "Installation Booked": "Install Booked",
  Installed: "Installed", "Balance Paid": "Done", Completed: "Done",
};
const SIMPLE_TO_ORDER = {
  "Order Placed": "Order Confirmed", Making: "Manufacturing",
  "Install Booked": "Installation Booked", Installed: "Installed", Done: "Completed",
};
export const simpleOrderStatus = (s) => ORDER_TO_SIMPLE[s] || s || "Order Placed";
export const orderStatusFor = (simple) => SIMPLE_TO_ORDER[simple] || simple;

export const StatusBadge = ({ s, simple }) => {
  const label = simple ? simpleStatus(s) : s || "New Lead";
  const styleKey = simple ? leadStatusFor(label) : s;
  return (
    <span
      data-testid={`status-badge-${slug(label)}`}
      className={`inline-flex items-center rounded-full px-2.5 py-0.5 text-[11px] font-bold whitespace-nowrap ${statusStyle(styleKey)}`}
    >
      {label}
    </span>
  );
};

export const gbp = (n) => `£${Number(n || 0).toLocaleString("en-GB", { maximumFractionDigits: 0 })}`;
export const gbp2 = (n) => `£${Number(n || 0).toFixed(2)}`;
export const fmtDate = (iso) =>
  iso ? new Date(iso).toLocaleString("en-GB", { day: "numeric", month: "short", hour: "2-digit", minute: "2-digit" }) : "—";
export const fmtDay = (iso) =>
  iso
    ? new Date(String(iso).length === 10 ? iso + "T00:00:00" : iso).toLocaleDateString("en-GB", { weekday: "short", day: "numeric", month: "short" })
    : "—";

export const inputCls =
  "w-full rounded-lg border border-warm-300 bg-white px-3 py-2.5 text-sm focus:border-navy-900 focus:outline-none";
export const btnPrimary =
  "inline-flex items-center justify-center gap-1.5 rounded-full bg-navy-900 px-4 py-2 text-xs font-semibold text-white hover:bg-navy-800 disabled:opacity-40 transition-colors";
export const btnGhost =
  "inline-flex items-center justify-center gap-1.5 rounded-full border border-warm-300 px-3.5 py-2 text-xs font-semibold text-slate-600 hover:border-navy-800/40 transition-colors";

export const Card = ({ title, testid, action, children, className = "" }) => (
  <section className={`rounded-2xl border border-warm-200 bg-white shadow-sm ${className}`} data-testid={testid}>
    {title && (
      <header className="flex items-center justify-between gap-3 px-5 pt-4 pb-3 border-b border-warm-200/70">
        <h2 className="font-serif text-lg text-navy-900">{title}</h2>
        {action}
      </header>
    )}
    <div className="p-5">{children}</div>
  </section>
);

export const EmptyState = ({ icon: Icon, children }) => (
  <div className="rounded-2xl border border-warm-200 bg-white p-10 text-center">
    {Icon && <Icon className="h-7 w-7 text-warm-300 mx-auto mb-2.5" />}
    <p className="text-sm text-slate-500">{children}</p>
  </div>
);

export const Spinner = () => (
  <div className="flex justify-center py-20">
    <div className="h-6 w-6 animate-spin rounded-full border-2 border-navy-900 border-t-transparent" />
  </div>
);
